import { SearchOutlined } from '@mui/icons-material';
import { CircularProgress, Grid, Skeleton, Typography } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';
import { useQuery } from 'react-query';
import {
  ICorrespondentResultViewModel,
  IDistrictViewModel,
  ISearchViewModel,
  ISpecialtyViewModel,
} from 'viewModels';
import { useTheme } from 'styled-components';
import { Button } from '../Buttons/Button';
import { CardCollaborator } from '../Cards/CardCollaborator';
import { Container, ContainerDropdown, ContentDropdown, GridCollaborators } from './style';
import SpecialtyController from '../../controller/SpecialtyController';
import { DropDownList } from '../DropDownList';
import { UserController } from '../../controller/UserController';
import { ModalSignUp } from '../Modals/ModalSignUp';
import { BRAZILIAN_STATES } from '../../constants';
import { DistrictController } from '../../controller/DistrictController';

interface InputSearchCorrespondences {
  districtUfCode: number;
  districtId: string;
  specialtyId?: string;
}

interface FindUserProps {
  redirectOnDetails?: boolean;
  showEmail?: boolean;
  showDescription?: boolean;
}

export function FindUser({
  redirectOnDetails = false,
  showEmail = false,
  showDescription = false,
}: FindUserProps) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [isModalSignupOpen, setIsModalSignupOpen] = useState(false);
  const [specialties, setSpecialties] = useState<ISpecialtyViewModel[]>([]);
  const [districts, setDistricts] = useState<IDistrictViewModel[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [inputSearch, setInputSearch] = useState<InputSearchCorrespondences | null>(null);

  // Schema de validação
  const InputSearchCorrespondencesSchema: yup.SchemaOf<InputSearchCorrespondences> = yup.object({
    districtUfCode: yup.number().moreThan(0, 'Selecione um Estado').required('Selecione o Estado'),
    districtId: yup.string().required('Selecione uma Comarca'),
    specialtyId: yup.string(),
  });

  const defaultValues: InputSearchCorrespondences = {
    districtUfCode: 0,
    districtId: '',
    specialtyId: '',
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<InputSearchCorrespondences>({
    defaultValues,
    resolver: yupResolver(InputSearchCorrespondencesSchema),
  });

  const [districtUfCode] = useWatch({
    control,
    name: ['districtUfCode'],
  });

  const { data: correspondentsData, isLoading } = useQuery<
    ISearchViewModel<ICorrespondentResultViewModel>
  >(
    ['correspondents', inputSearch, currentPage],
    () => {
      if (!inputSearch) {
        return Promise.resolve({
          totalPages: 0,
          totalItems: 0,
          currentPage: 1,
          pageSize: 18,
          items: [] as ICorrespondentResultViewModel[],
        });
      }
      return UserController.getCorrespondents({
        districtId: inputSearch.districtId,
        specialtyId: inputSearch.specialtyId,
        page: currentPage,
        take: 18,
      });
    },
    {
      enabled: !!inputSearch?.districtId,
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    },
  );

  const handleSubmitSearch = (input: InputSearchCorrespondences) => {
    setInputSearch(input);
    setCurrentPage(1);
  };

  // Busca os itens dos dropdowns quando o componente renderiza
  useEffect(() => {
    SpecialtyController.getAll({}).then((data) =>
      setSpecialties(data.sort((a, b) => a.name.localeCompare(b.name))),
    );
  }, []);

  useEffect(() => {
    setDistricts([]);
    setValue('districtId', '');
    if (districtUfCode !== 0) {
      DistrictController.getDistrictsByUfCode(districtUfCode)
        .then((res) => {
          setDistricts(res);
        })
        .finally(() => {});
    }
  }, [districtUfCode, setValue]);

  return (
    <Container onSubmit={handleSubmit(handleSubmitSearch)}>
      <div>
        <ContainerDropdown>
          <ContentDropdown>
            <Controller
              control={control}
              name="districtUfCode"
              render={({ field: { onChange } }) => (
                <DropDownList
                  placeholderTitle="Estado"
                  isSearchable
                  itens={BRAZILIAN_STATES.map((s) => ({ id: s.ufCode, value: s.name })) ?? []}
                  width="100%"
                  typeReturn="id"
                  onChange={onChange}
                  errorText={errors.districtUfCode?.message}
                />
              )}
            />
          </ContentDropdown>
          <ContentDropdown>
            <Controller
              control={control}
              name="districtId"
              render={({ field: { onChange, value } }) => (
                <DropDownList
                  value={value}
                  isSearchable
                  itens={districts
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((district) => ({
                      id: district.id,
                      value: district.name,
                    }))}
                  placeholderTitle="Selecione a Comarca"
                  typeReturn="id"
                  disabled={districts.length <= 0}
                  onChange={onChange}
                  errorText={errors.districtId?.message}
                />
              )}
            />
          </ContentDropdown>
          <ContentDropdown>
            <Controller
              name="specialtyId"
              control={control}
              render={({ field: { onChange } }) => (
                <DropDownList
                  placeholderTitle="Especialidade"
                  itens={specialties.map((c) => ({ id: c.id, value: c.name }))}
                  typeReturn="id"
                  isSearchable
                  onChange={onChange}
                />
              )}
            />
          </ContentDropdown>
        </ContainerDropdown>
      </div>

      <div className="containerButton">
        <Button
          disabled={isLoading}
          text="Buscar"
          dimensions={{
            width: '10rem',
            height: '3rem',
          }}
          font={{ weight: 600 }}
          LeftComponent={isLoading ? CircularProgress : SearchOutlined}
          styleLeftComponent={
            isLoading
              ? {
                  width: 15,
                  height: 15,
                  color: 'white',
                }
              : { color: 'white' }
          }
          positionLeftElement={10}
          background="tertiary_500"
          color="background_white"
          border={{ color: 'tertiary_500', width: 1 }}
          textHoverColor="background_white"
          backgroundHoverColor="secondary"
          type="submit"
        />
      </div>
      <div>
        {isLoading ? (
          <GridCollaborators>
            {Array.from({ length: 18 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Grid item xs={12} key={`skeleton-${index}`}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="8rem"
                  sx={{ borderRadius: '8px' }}
                />
              </Grid>
            ))}
          </GridCollaborators>
        ) : inputSearch && correspondentsData?.items.length === 0 ? (
          <Grid container padding="1rem" justifyContent="center">
            <Typography justifyContent="center" align="center" color={colors.primary}>
              Nenhum correspondente encontrado, infelizmente...
            </Typography>
          </Grid>
        ) : (
          <GridCollaborators>
            {correspondentsData?.items.map((correspondent: ICorrespondentResultViewModel) => (
              <CardCollaborator
                key={correspondent.id}
                currentUser={correspondent}
                onViewDetails={() => {
                  if (redirectOnDetails) setIsModalSignupOpen(true);
                }}
                showEmail={showEmail}
                showDescription={showDescription}
              />
            ))}
          </GridCollaborators>
        )}
      </div>

      <div className="pagination">
        {correspondentsData && correspondentsData.items.length > 0 && (
          <Pagination
            size="small"
            count={correspondentsData.totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            defaultPage={1}
            boundaryCount={2}
          />
        )}
      </div>

      {isModalSignupOpen && (
        <ModalSignUp
          isOpen={isModalSignupOpen}
          onConfirm={() => {
            setIsModalSignupOpen(false);
            navigate('/register');
          }}
          onClose={() => {
            setIsModalSignupOpen(false);
          }}
        />
      )}
    </Container>
  );
}
